import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { ContentType } from '../content/contracts';
/**
 * `GET /api/me/saved` — every line a member kept, across sources.
 *
 * ── One list over two tables ─────────────────────────────────────────────
 * A kept line is either a content item (a `SAVE` signal) or a community post
 * (a bookmark). The server unions them and decides `source` from the ROW, not
 * from `contentType` — which matters, because three of the five tabs are
 * content items and inferring the tab from the type would collapse them.
 *
 * ── `line` is a title, except when it is a sentence ──────────────────────
 * A save names a whole item; there is nowhere to record an offset into a body,
 * so a card shows the item's TITLE where the design's mock shows a sentence.
 * The exception is a phrase, whose title *is* the sentence — which is why the
 * design's own examples all read as lines. Keeping a pull-quote out of a
 * course module needs addressable positions in the body and is a feature, not
 * a field.
 *
 * ── `COACH` selects nothing, on purpose ──────────────────────────────────
 * There is no coach on this platform — no model, no route, no table. The tab
 * exists and answers empty rather than borrowing rows from another source to
 * look populated.
 *
 * ── Re-scoped on every read ──────────────────────────────────────────────
 * A room narrowed after the bookmark, a lapsed subscription or an item gone
 * premium drops the row from the list and leaves it in the table — it is the
 * parent's record, not ours to delete. `total` counts what they can still
 * open, and is the same number `Journey.anchorsSaved` reports.
 */
export const SavedSource = z.enum(['PHRASE', 'COURSE', 'LIBRARY', 'COMMUNITY', 'COACH']);
export const SavedItem = z.object({
    id: z.string().uuid(),
    source: SavedSource,
    /** The kept line — an item's title, or a phrase's own text. */
    line: z.string(),
    contentType: ContentType.nullable(),
    /** The API's own path segment, so a link is not assembled from `contentType`. */
    path: z.string().nullable(),
    /** Who said it, for a community line. Null for content. */
    saidBy: z.string().nullable(),
    roomId: z.string().uuid().nullable(),
    savedAt: z.string().datetime(),
});
export const SavedList = z.object({
    items: z.array(SavedItem),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    locale: z.string().optional(),
});
export function useSaved(http, locale, options = {}) {
    const { source, q, pageSize } = options;
    return useQuery({
        queryKey: ['me', 'saved', locale, source ?? 'all', q ?? '', pageSize ?? 20],
        queryFn: () => http.get('/me/saved', {
            schema: SavedList,
            locale,
            query: {
                ...(source ? { source } : {}),
                ...(q ? { q } : {}),
                ...(pageSize ? { pageSize } : {}),
            },
        }),
        staleTime: 60_000,
    });
}
export function savedKind(item) {
    return item.source === 'COMMUNITY' ? 'POST' : 'CONTENT';
}
const SAVED_PREFIX = ['me', 'saved'];
export function useRemoveSaved(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (item) => http.post('/me/saved/remove', { id: item.id, kind: savedKind(item) }, { schema: z.object({ ok: z.boolean() }).passthrough() }),
        /*
         * Optimistic, across EVERY cached page of the list — each tab and each
         * search is its own cache entry, and the row is in several of them. The
         * snapshots put them all back if the server refuses.
         */
        onMutate: async (item) => {
            await queryClient.cancelQueries({ queryKey: SAVED_PREFIX });
            const snapshots = queryClient.getQueriesData({ queryKey: SAVED_PREFIX });
            for (const [key, list] of snapshots) {
                if (!list?.items.some((row) => row.id === item.id))
                    continue;
                queryClient.setQueryData(key, {
                    ...list,
                    items: list.items.filter((row) => row.id !== item.id),
                    total: Math.max(0, list.total - 1),
                });
            }
            return { snapshots };
        },
        onError: (_error, _item, context) => {
            for (const [key, list] of context?.snapshots ?? [])
                queryClient.setQueryData(key, list);
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: SAVED_PREFIX });
            // `Journey.anchorsSaved` counts the same union, and a post's bookmark
            // list is one of the ways it was kept.
            void queryClient.invalidateQueries({ queryKey: ['journey'], exact: true });
            void queryClient.invalidateQueries({ queryKey: ['community', 'bookmarks'] });
        },
    });
}
//# sourceMappingURL=saved.js.map